/**
 * GiDetailPage — read-only view of a single GI.
 *
 * Phase 1 shows header, manifest, contract summary, and lineage
 * timeline. Attestations are stubbed because the engine's M8
 * (gi-attestor) is still paused; the timeline renders "no
 * attestations yet" so the shape is there for later fill-in.
 */

import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Card, CardSubtitle, CardTitle } from '@/components/Card';
import { DomainBadge, StatusBadge } from '@/components/Badge';
import { DomainIcon } from '@/components/DomainIcon';
import { ErrorState } from '@/components/ErrorState';
import { HashDisplay } from '@/components/HashDisplay';
import { Spinner } from '@/components/LoadingState';

import { useGiDetail } from '@/hooks/useVault';
import { useLocaleStore } from '@/stores/localeStore';
import {
  formatAbsoluteTime,
  normalizeDomain,
  relativeFromTimestamp,
} from '@/utils/format';

export function GiDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['vault', 'common', 'errors']);
  const locale = useLocaleStore((s) => s.locale);
  const query = useGiDetail(id);

  if (query.isPending) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="mx-auto max-w-2xl">
        <ErrorState
          title={t('not_found.title', { ns: 'errors' })}
          body={t('not_found.body', { ns: 'errors' })}
          actions={
            <Link to="/" className="contents">
              <Button variant="secondary" size="sm">
                {t('nav.vault', { ns: 'common' })}
              </Button>
            </Link>
          }
        />
      </div>
    );
  }

  const { manifest, signed_entity, contract, lineage } = query.data;
  const normalized = normalizeDomain(manifest.domain);
  const domainLabel =
    typeof manifest.domain === 'string'
      ? t(`domain.${manifest.domain}`, { ns: 'common' })
      : manifest.domain.Custom;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <Link to="/" className="text-sm text-ink-muted hover:text-ink">
        ← {t('nav.vault', { ns: 'common' })}
      </Link>

      <header className="flex items-start gap-4">
        <span
          className={`flex h-12 w-12 items-center justify-center rounded-lg bg-domain-${normalized}-soft text-domain-${normalized}`}
        >
          <DomainIcon domain={normalized} className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <DomainBadge domain={normalized} label={domainLabel} />
            {query.data.status.map((s) => (
              <StatusBadge
                key={s}
                status={s}
                label={t(`status.${s}`, { ns: 'common' })}
              />
            ))}
          </div>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
            {manifest.name}
          </h1>
          <p className="mt-1 text-sm text-ink-muted">{manifest.description}</p>
        </div>
      </header>

      <Card>
        <CardTitle>Manifest</CardTitle>
        <CardSubtitle>
          v{manifest.version} · {manifest.author.name} ·{' '}
          <time
            dateTime={manifest.created_at.iso}
            title={formatAbsoluteTime(manifest.created_at.iso, locale)}
          >
            {relativeFromTimestamp(manifest.created_at, locale)}
          </time>
        </CardSubtitle>
        <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="root_id" value={manifest.root_id} />
          <Field label="manifest_hash" value={signed_entity.manifest_hash} />
          <Field label="contract_hash" value={manifest.contract_hash} />
          <Field label="wasm_hash" value={manifest.wasm_hash} />
          <Field label="source_hash" value={manifest.source_hash} />
          <Field label="signer_public_key" value={signed_entity.signer_public_key} />
        </dl>
      </Card>

      <Card>
        <CardTitle>Contract</CardTitle>
        <CardSubtitle>
          {contract.inputs.length} inputs · {contract.outputs.length} outputs ·{' '}
          {contract.parameters.length} parameters
        </CardSubtitle>
      </Card>

      <Card>
        <CardTitle>Lineage</CardTitle>
        <CardSubtitle>
          {lineage.entries.length} entr{lineage.entries.length === 1 ? 'y' : 'ies'}
        </CardSubtitle>
        <ol className="mt-3 space-y-2 border-l border-border ps-4">
          {lineage.entries.map((e) => (
            <li key={e.index} className="text-sm">
              <div className="flex items-center justify-between">
                <span className="font-medium text-ink">
                  {e.index === 0 ? 'Genesis' : 'Update'} · v{e.version}
                </span>
                <span className="text-xs text-ink-subtle">
                  {relativeFromTimestamp(e.signed_at, locale)}
                </span>
              </div>
              <div className="mt-1">
                <HashDisplay value={e.entry_hash} />
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <Card>
        <CardTitle>Attestations</CardTitle>
        <CardSubtitle>
          Every signed execution, with witness status, hashes, and export.
        </CardSubtitle>
        <div className="mt-3">
          <Link to={`/gi/${manifest.root_id}/attestations`} className="contents">
            <Button variant="secondary" size="sm">
              Open attestation viewer
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wider text-ink-subtle">{label}</dt>
      <dd className="mt-1">
        <HashDisplay value={value} />
      </dd>
    </div>
  );
}
